import React from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, 
  Eye, 
  Clock, 
  CheckCircle, 
  AlertCircle
} from 'lucide-react';

const JobCard = ({ 
  job, 
  variant = 'detailed', // 'compact' for dashboard, 'detailed' for history
  onClick,
  isHighlighted = false 
}) => {
  if (!job) return null;

  const isProcessing = job.status === 'pending' || job.status === 'processing';
  const progressPercentage = job.progress.totalPhotos > 0 
    ? Math.round(((job.progress.processedPhotos + job.progress.skippedPhotos + job.progress.failedPhotos) / job.progress.totalPhotos) * 100)
    : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCardBackground = () => {
    if (isHighlighted) return 'bg-blue-50 border-blue-200';
    if (isProcessing) return 'bg-yellow-50 border-yellow-200';
    return 'bg-white border-gray-200';
  };

  // Compact variant for dashboard
  if (variant === 'compact') {
    return (
      <div 
        className={`px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${onClick ? '' : 'pointer-events-none'}`}
        onClick={() => onClick?.(job._id)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                {job.status}
              </span>
              <span className="ml-3 text-sm text-gray-500">
                {job.progress.processedPhotos} of {job.progress.totalPhotos} photos processed
              </span>
              <span className="ml-3 text-xs text-gray-400 capitalize">
                {job.config?.style || 'descriptive'} style
                {job.config?.descriptionSentences && (
                  <span className="ml-1 text-gray-400">• {job.config.descriptionSentences} sentences</span>
                )}
              </span>
            </div>
            
            {/* Thumbnail Grid */}
            {job.thumbnails && job.thumbnails.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {job.thumbnails.map((thumbnail, thumbIndex) => (
                  <div
                    key={thumbIndex}
                    className="relative w-10 h-10 rounded-md overflow-hidden bg-gray-100 border border-gray-200 hover:border-gray-300 transition-colors group"
                    title={thumbnail.title}
                  >
                    <img
                      src={thumbnail.url}
                      alt={thumbnail.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNy4zMzMzIDEyLjhMMjEuMzMzMyAxNi44TDI1LjYgMTIuOEwyOS44NjY3IDE2LjhMMzQuMTMzMyAxMi44VjI1LjZINi41MzMzM1YxMi44SDE3LjMzMzNaIiBmaWxsPSIjRDFENURCIi8+CjxjaXJjbGUgY3g9IjE0LjkzMzMiIGN5PSIxNi44IiByPSIyLjEzMzMzIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';
                      }}
                    />
                  </div>
                ))}
                {job.progress.processedPhotos > (job.thumbnails?.length || 0) && (
                  <div className="w-10 h-10 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center">
                    <span className="text-xs text-gray-400 font-medium">
                      +{job.progress.processedPhotos - (job.thumbnails?.length || 0)}
                    </span>
                  </div>
                )}
              </div>
            )}
            

          </div>
          
          <span className="text-sm text-gray-500 ml-4 flex-shrink-0">
            {new Date(job.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    );
  }

  // Detailed variant for history page
  return (
    <div className={`rounded-lg shadow-sm border ${getCardBackground()}`}>
      <div className="flex items-start justify-between p-4">
        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {job.progress.totalPhotos} Photo{job.progress.totalPhotos === 1 ? '' : 's'} Enhanced
            </h3>
            <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </span>
            {isProcessing && (
              <div className="ml-2 flex items-center text-sm text-yellow-600">
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                Processing
              </div>
            )}
          </div>
          
          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
            <span>Created: {new Date(job.createdAt).toLocaleString()}</span>
            <span>Style: {job.config?.style || 'descriptive'}
              {job.config?.descriptionSentences && (
                <span className="ml-1 text-gray-400">• {job.config.descriptionSentences} sentences</span>
              )}
            </span>
          </div>
          
          {/* Thumbnail Grid for detailed view */}
          {job.thumbnails && job.thumbnails.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {job.thumbnails.map((thumbnail, thumbIndex) => (
                <div
                  key={thumbIndex}
                  className="relative w-12 h-12 rounded-md overflow-hidden bg-gray-100 border border-gray-200 hover:border-gray-300 transition-colors group"
                  title={thumbnail.title}
                >
                  <img
                    src={thumbnail.url}
                    alt={thumbnail.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAxNi4wTDI0IDE5LjZMMjggMTYuMEwzMiAxOS42TDM2IDE2LjBWMjkuNkg4VjE2LjBIMjBaIiBmaWxsPSIjRDFENURCIi8+CjxjaXJjbGUgY3g9IjE3LjYiIGN5PSIxOS42IiByPSIyLjQiIGZpbGw9IiNEMUQ1REIiLz4KPC9zdmc+';
                    }}
                  />
                </div>
              ))}
              {job.progress.processedPhotos > (job.thumbnails?.length || 0) && (
                <div className="w-12 h-12 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center">
                  <span className="text-xs text-gray-400 font-medium">
                    +{job.progress.processedPhotos - (job.thumbnails?.length || 0)}
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Summary Stats */}
          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600">
            <span className="text-green-600">
              ✓ {job.progress.processedPhotos} processed
            </span>
            {job.progress.skippedPhotos > 0 && (
              <span className="text-yellow-600">
                ⚠ {job.progress.skippedPhotos} skipped
              </span>
            )}
            {job.progress.failedPhotos > 0 && (
              <span className="text-red-600">
                ✗ {job.progress.failedPhotos} failed
              </span>
            )}
          </div>

          {/* Progress bar for processing jobs */}
          {isProcessing && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progress: {progressPercentage}%</span>
                <span>Batch {job.progress.currentBatch || 1}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}


        </div>
        
        {/* View Details Button */}
        <div className="ml-4">
          <Link
            to={`/job/${job._id}`}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Eye className="mr-1 h-4 w-4" />
            View Results
          </Link>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
